import React, { useEffect, useState } from 'react';
import './UsersBrowser.css';
import * as api from 'api.js';
import { useGlobalState } from 'hooks';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUsers, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import * as cloneDeep from 'lodash.clonedeep';
import { UserCreateModal } from 'components/UserManager/UserManagerModals';

export function UsersBrowser() {
    const [apiError, setApiError] = useState('');
    const [state, dispatch] = useGlobalState();
    const [show, setShow] = useState(false);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [userToBeDeleted, setUserToBeDeleted] = useState('');

    useEffect(() => {
        dispatch.setBusy(true); // *
        const getUserList = async () => {
            try {
                const userList = await api.getUserList();
                const parsedUsers = parseUserList(userList);
                dispatch.setUsers(parsedUsers);
            } catch (error) {
                if (error.response) {
                    setApiError(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        getUserList(); // *
    }, [state.refresh]);

    function handleUserDelete(username) {
        setUserToBeDeleted(username);
        setShow(true);
    }

    if (apiError) {
        return (
            <>
                <h3>API error:</h3>
                {apiError}
            </>
        );
    }

    return (
        <div className="users_browser_main_cont">
            <div className="browser_main_upper">
                <div className="blue_top_bar">
                    <>
                        <FontAwesomeIcon icon={faUsers} />
                        &nbsp; ALL USERS
                    </>
                </div>
                <UserList deleteHandler={handleUserDelete} />
            </div>
            <div className="browser_main_lower">
                <button type="button" className="styled_button" onClick={() => setShowCreateUser(true)}>
                    <FontAwesomeIcon icon={faUserPlus} />
                    &nbsp;ADD NEW USER
                </button>
                <UserCreateModal show={showCreateUser} setShow={setShowCreateUser} />
            </div>
            <UserDeleteModal show={show} setShow={setShow} username={userToBeDeleted} />
        </div>
    );
}

function UserList({ deleteHandler }) {
    const [state, dispatch] = useGlobalState();
    const [sortedUsers, setSortedUsers] = useState([]);
    const [sortBool, setSortBool] = useState(true);

    useEffect(() => {
        sortByUserName();
    }, [state.users]);

    function handleClick(userObj) {
        dispatch.setSelectedUser(userObj);
    }

    function sortByUserName() {
        let usersClone = cloneDeep(state.users);
        usersClone.sort((a, b) => {
            if (a.username.toUpperCase() < b.username.toUpperCase()) {
                return sortBool ? 1 : -1;
            }
            if (a.username.toUpperCase() > b.username.toUpperCase()) {
                return sortBool ? -1 : 1;
            }
            return 0;
        });
        setSortedUsers(usersClone);
        setSortBool(!sortBool);
    }

    function sortByCompanyName() {
        let usersClone = cloneDeep(state.users);
        usersClone.sort((a, b) => {
            let companyA = a.company ? a.company.toUpperCase() : '';
            let companyB = b.company ? b.company.toUpperCase() : '';
            return sortBool ? companyA.localeCompare(companyB) : companyB.localeCompare(companyA);
        });
        setSortedUsers(usersClone);
        setSortBool(!sortBool);
    }

    return (
        <>
            <div className="list_heading">
                <div className="user_list_name" onClick={() => sortByUserName()}>
                    {' '}
                    USERNAME{' '}
                </div>
                <div className="user_list_company" onClick={() => sortByCompanyName()}>
                    {' '}
                    COMPANY{' '}
                </div>
                <div className="users_scrollbar_padding"></div>
            </div>

            <div className="users_browser_user_list">
                {sortedUsers.map((user) => (
                    <UserListItem
                        key={user.username}
                        user={user}
                        deleteHandler={deleteHandler}
                        clickHandler={handleClick}
                        selected={user.username === state.selectedUser.username}
                    />
                ))}
            </div>
        </>
    );
}

function UserListItem({ user, clickHandler, deleteHandler, selected }) {
    return (
        <div className={selected ? 'selected_user_list_item' : 'user_list_item'}>
            <div className="user_list_name" onClick={() => clickHandler(user)}>
                {user.username}
            </div>
            <div className="user_list_company" onClick={() => clickHandler(user)}>
                {user.company}
            </div>
            <div
                className="user_list_remove_icon"
                onClick={() => {
                    clickHandler(user);
                    deleteHandler(user.username);
                }}
            >
                <FontAwesomeIcon icon={faTrash} />
            </div>
        </div>
    );
}

export const UserDeleteModal = ({ show, setShow, username }) => {
    const [state, dispatch] = useGlobalState();
    const [submit, setSubmit] = useState(false);

    useEffect(() => {
        if (!submit) return;
        dispatch.setBusy(true);
        const deleteUser = async () => {
            try {
                const response = await api.deleteUser(username);
                cleanUp();
                dispatch.refreshUserBrowser();
                dispatch.setSelectedUser({ username: null });
            } catch (error) {
                if (error.response) {
                    console.log(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        deleteUser();
    }, [submit]);

    const cleanUp = () => {
        setShow(false);
        setSubmit(false);
    };

    return (
        <div>
            <Modal show={show}>
                <Modal.Header>Remove user {username}?</Modal.Header>
                <Modal.Body></Modal.Body>
                <Modal.Footer>
                    <button className="styled_button" onClick={cleanUp}>
                        Cancel
                    </button>
                    <button className="styled_button" onClick={() => setSubmit(true)}>
                        Remove user
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

function parseUserList(userObj) {
    return userObj.Users.map(user => {
        let attributes = user.Attributes;
        return {
            username: user.Username,
            email: findAttribute(attributes, 'email'),
            phone: findAttribute(attributes, 'phone_number'),
            company: findAttribute(attributes, 'custom:company')
        };
    });
}

function findAttribute(attributes, attributeName) {
    const attr = attributes.find(attr => attr.Name === attributeName);
    return attr ? attr.Value : null;
}
